import React, { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { hideDeleteAb } from 'reactApp/appHelpers/featuresHelpers/features/overquotaHideDeleteAb';
import { overquotaSafeTip } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { sendDwhButton } from 'reactApp/modules/dwh/dwh.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getCurrentGroupId, getIsLetterGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { Move2SafeTooltip } from 'reactApp/ui/Toolbar/Move2SafeButton/Move2SafeTooltip';

const name = 'mnz_file_to_trash';

interface DeleteButtonProps {
    items: CloudItem[];
    totalSize?: number;
    totalLength?: number;
    hook: ({ items, totalSize, totalLength }) => {
        callback: () => void;
        title?: string;
        isMove2SafeEnabled?: boolean;
        deleteSize: {
            size: number;
            sizeString: string;
        };
    };
}

/**
 * Кнопка тулбара - Удаление чего-либо
 * @param hook - хук, который вернет коллбек (действие удаления), удаляемый размер
 * @param items - массив удаляемых элементов
 */
export const DeleteButton: FC<DeleteButtonProps> = ({ hook, items, totalSize, totalLength }) => {
    const { callback, deleteSize, title, isMove2SafeEnabled } = hook({ items, totalSize, totalLength });
    const { size, sizeString } = deleteSize;
    const ref = useRef<HTMLDivElement>(null);
    const storage = useSelector(getCurrentStorage);
    const quotaCleanerIsLetterGroup = useSelector(getIsLetterGroup);
    const groupId = useSelector(getCurrentGroupId);

    const hideDelete =
        hideDeleteAb && storage === EStorageType.quotaCleaner && !quotaCleanerIsLetterGroup && groupId !== UserQuotaGroupId.CloudBin;
    const enableSafeTip = !hideDelete && isMove2SafeEnabled && overquotaSafeTip && storage === EStorageType.quotaCleaner && !!size;

    const sizeText = useMemo(() => {
        if (!size) {
            return '';
        }

        return `\xA0${sizeString}`;
    }, [size, sizeString]);

    useEffect(() => {
        if (hideDelete) {
            sendDwhButton({ name, source: 'cleaner' });
        } else if (title) {
            sendDwhButton({ name: 'files_to_safe', source: 'cleaner' });
        }
    }, [title, hideDelete]);

    const handleClick = useCallback(() => {
        callback();

        if (hideDelete) {
            sendDwhButton({ action: 'click', name, source: 'cleaner' });
        } else if (title) {
            sendDwhButton({ action: 'click', name: 'files_to_safe', source: 'cleaner' });
        }
    }, [callback, title, hideDelete]);

    const button = (
        <Button theme="octavius" primary sizeMode={ButtonSizeMode.small} disabled={!size} onClick={handleClick} qaId="delete-btn">
            {hideDelete ? 'Переместить в корзину' : title || 'Удалить'}
            {sizeText}
        </Button>
    );

    if (enableSafeTip) {
        return (
            <div ref={ref}>
                {button}
                <Move2SafeTooltip targetRef={ref} />
            </div>
        );
    }

    return button;
};
